//log-In-page.tsx

import { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  TextField,
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Link,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { sessionsApi } from "../services/api/sessions";
import { LogoHeader } from "../components/logo-header";
import axios from "axios";

export function LogInPage(props: { onLoggedIn: (from?: string) => void }) {
  const location = useLocation<{ from: string }>();

  const form = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: yup.object({
      email: yup
        .string()
        .email("Enter a valid email")
        .required("Email is required"),
      password: yup.string().required("Password is required"),
    }),
    onSubmit: () => {
      logIn();
    },
  });

  const [error, setError] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);

  const logIn = async () => {
    setError("");
    setBusy(true);
    try {
      await sessionsApi.start(form.values.email, form.values.password);
      props.onLoggedIn(location.state?.from);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const message = (err.response?.data as any)?.message;

        if (status === 401) {
          setError("Incorrect email or invalid password");
        } else if (status === 403) {
          setError(
            message ||
              "Your access has been removed. Please contact the team owner.",
          );
        } else {
          setError(
            "Something went wrong. Check your connection or try again later",
          );
        }
      } else {
        setError(
          "Something went wrong. Check your connection or try again later",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <LogoHeader />

      <Paper elevation={3}>
        <Box sx={{ mt: 4, p: 3, textAlign: "center" }}>
          <Typography gutterBottom variant="h5" component="h2">
            Log In
          </Typography>

          <Box component="form" onSubmit={form.handleSubmit} noValidate>
            <Box sx={{ pt: 2 }}>
              <TextField
                id="email"
                name="email"
                type="email"
                label="Email"
                value={form.values.email}
                onChange={form.handleChange}
                fullWidth
                variant="outlined"
                autoFocus
                error={form.touched.email && Boolean(form.errors.email)}
                helperText={form.touched.email && form.errors.email}
              />
            </Box>

            <Box sx={{ pt: 2 }}>
              <TextField
                id="password"
                name="password"
                type="password"
                label="Password"
                value={form.values.password}
                onChange={form.handleChange}
                fullWidth
                variant="outlined"
                error={form.touched.password && Boolean(form.errors.password)}
                helperText={form.touched.password && form.errors.password}
              />
            </Box>

            {error && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" color="error">
                  {error}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                color="primary"
                type="submit"
                disabled={busy}
              >
                Log In
              </Button>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Box component="span" sx={{ p: 1 }}>
                <Link component={RouterLink} to="/sign-up" underline="hover">
                  Sign Up
                </Link>
              </Box>
              <Box component="span" sx={{ p: 1 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  underline="hover"
                >
                  Forgot Password
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
